// src/components/reports/WorkStatsPanel.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, Row, Col, Table, Tabs, Tab, Badge } from 'react-bootstrap';
import { FaHourglassHalf, FaCheckCircle } from 'react-icons/fa';

const PENDING_STATUSES = ['PENDING', 'ASSIGNED', 'ACCEPTED_BY_EXAMINER', 'WORK_UNDER_REVIEW'];
const COMPLETED_STATUSES = ['COMPLETED'];

function aggregate(rows, keyId, labelFn) {
  const map = new Map();
  rows.forEach((r) => {
    const id = r[keyId];
    if (id === null || id === undefined) return;
    if (!map.has(id)) map.set(id, { label: labelFn(r), pending: 0, completed: 0, other: 0 });
    const bucket = map.get(id);
    if (PENDING_STATUSES.includes(r.status)) bucket.pending += 1;
    else if (COMPLETED_STATUSES.includes(r.status)) bucket.completed += 1;
    else bucket.other += 1;
  });
  return Array.from(map.values()).sort((a, b) => (b.pending + b.completed) - (a.pending + a.completed));
}

// Pending vs. completed work stats, per series / module / exam centre.
// Shared between the Admin and Archiver dashboards.
export default function WorkStatsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get('/api/dashboard/assignment-stats')
      .then((res) => {
        setRows(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching work statistics.');
        setLoading(false);
      });
  }, []);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        if (PENDING_STATUSES.includes(r.status)) acc.pending += 1;
        else if (COMPLETED_STATUSES.includes(r.status)) acc.completed += 1;
        return acc;
      },
      { pending: 0, completed: 0 }
    );
  }, [rows]);

  const bySeries = useMemo(
    () => aggregate(rows, 'seriesId', (r) => r.seriesName || `#${r.seriesId}`),
    [rows]
  );
  const byModule = useMemo(
    () => aggregate(rows, 'moduleId', (r) => `${r.moduleCode || ''} - ${r.moduleName || `#${r.moduleId}`}`),
    [rows]
  );
  const byCentre = useMemo(
    () => aggregate(rows, 'examCentreId', (r) => `${r.examCentreNumber || ''} - ${r.examCentreName || `#${r.examCentreId}`}`),
    [rows]
  );

  const breakdownTable = (data, labelHeader) => (
    <Table striped bordered hover size="sm" responsive>
      <thead>
        <tr>
          <th>{labelHeader}</th>
          <th>Pending</th>
          <th>Completed</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td colSpan={4} className="text-center text-muted">No data</td>
          </tr>
        ) : (
          data.map((b, idx) => (
            <tr key={idx}>
              <td>{b.label}</td>
              <td>
                <Badge bg={b.pending > 0 ? 'warning' : 'secondary'} text={b.pending > 0 ? 'dark' : undefined}>
                  {b.pending}
                </Badge>
              </td>
              <td>
                <Badge bg="success">{b.completed}</Badge>
              </td>
              <td>{b.pending + b.completed}</td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );

  if (loading) return <p>Loading work statistics...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  return (
    <div className="mb-4">
      <Row className="row-cols-1 row-cols-md-2 g-3 mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <FaHourglassHalf size={28} className="text-warning mb-2" />
              <h5 className="card-title fw-bold mb-2">Pending Work</h5>
              <p className="display-6 text-warning">{totals.pending}</p>
              <p className="small text-muted">Assignments awaiting examiner completion</p>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <FaCheckCircle size={28} className="text-success mb-2" />
              <h5 className="card-title fw-bold mb-2">Completed Work</h5>
              <p className="display-6 text-success">{totals.completed}</p>
              <p className="small text-muted">Assignments marked done by the examiner</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Tabs defaultActiveKey="series" className="mb-3">
            <Tab eventKey="series" title="Per Series">
              {breakdownTable(bySeries, 'Series')}
            </Tab>
            <Tab eventKey="module" title="Per Module">
              {breakdownTable(byModule, 'Module')}
            </Tab>
            <Tab eventKey="centre" title="Per Exam Centre">
              {breakdownTable(byCentre, 'Exam Centre')}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
}
